import React, { useMemo } from 'react';
import SortableTable from '../SortableTable';
import useVisitsTargetsAnalysis from '../../hooks/useVisitsTargetsAnalysis';
import { isCompletedVisit } from '../../constants/visitsClassification';
import { countStoreVisits, physicalVisitDurations } from '../../constants/visitsUnivers';

const VisitsSectorAnalysis = ({
  visits,
  startDate,
  endDate,
  selectedProject,
  selectedShopSigns,
  fetchWithAuth,
}) => {

  const { targets, loading: targetsLoading } = useVisitsTargetsAnalysis({
    visits,
    startDate,
    endDate,
    selectedProject,
    selectedShopSigns,
    fetchWithAuth
  });

  const sectorAnalysis = useMemo(() => {
    if (!targets || targets.length === 0) return [];

    const periodDays = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1;

    const visitsBySectorByStore = {};
    const shopSignValues = selectedShopSigns && selectedShopSigns.length > 0
      ? selectedShopSigns.map(sign => sign.value)
      : null;

    const visitsForAnalysis = shopSignValues
      ? visits.filter(visit => visit.shopSign && shopSignValues.includes(visit.shopSign))
      : visits;

    const completedVisits = visitsForAnalysis.filter(isCompletedVisit);
    completedVisits.forEach(visit => {
      if (!visit.interventionPoint) return;

      const sectorName = visit.sectorName || "Non défini";

      if (!visitsBySectorByStore[sectorName]) {
        visitsBySectorByStore[sectorName] = {};
      }

      if (!visitsBySectorByStore[sectorName][visit.interventionPoint]) {
        visitsBySectorByStore[sectorName][visit.interventionPoint] = {
          visits: [],
        };
      }

      visitsBySectorByStore[sectorName][visit.interventionPoint].visits.push(visit);
    });

    // Duration over the rows the univers lens selects, and counted over the same
    // rows — see visitsWithDuration(). Summing the repeated actualDurationMinutes
    // made this table disagree with the Résumé under a Blanc/Brun filter.
    // PHYSICAL durations: this table scores stores against `visit_duration`, the
    // same 45-180 min per-visit target the Objectifs tab reads. Measuring it
    // against one univers' allocated share gave the two tabs opposite verdicts on
    // the same store — Sur-durée there, Sous-durée here.
    //
    // Keyed by SECTOR AND store: the table is per sector, so a global key gave a
    // store worked by two sectors the cross-sector mean in both rows.
    const measuredByStore = new Map();
    const measureKey = (sector, store) => `${sector}|${store}`;
    physicalVisitDurations(completedVisits).forEach((v) => {
      if (!v.interventionPoint) return;
      const key = measureKey(v.sectorName || "Non défini", v.interventionPoint);
      const acc = measuredByStore.get(key) || { total: 0, count: 0 };
      acc.total += v.durationMinutes;
      acc.count += 1;
      measuredByStore.set(key, acc);
    });

    const sectorAnalysisResult = [];
    Object.keys(visitsBySectorByStore).forEach(sectorName => {
      const storeVisits = visitsBySectorByStore[sectorName];

      let underFrequencyCount = 0;
      let conformFrequencyCount = 0;
      let overFrequencyCount = 0;
      let underDurationCount = 0;
      let conformDurationCount = 0;
      let overDurationCount = 0;
      let totalVisitedStores = 0;
      let storesWithTargets = 0;
      let storesWithDurationTargets = 0;

      targets.forEach(target => {
        const storeName = target.intervention_point_name;

        if (storeVisits[storeName]) {
          totalVisitedStores++;
          storesWithTargets++;

          const visits = storeVisits[storeName].visits;
          const targetFrequency = target.visit_frequency;
          const expectedVisitsInPeriod = (targetFrequency * periodDays) / 365;

          // Physical visits, like the duration above and like the Objectifs tab:
          // `visit_frequency` is a per-store target, so a Blanc+Brun visit
          // counted twice reported "Sur-visite" for a store exactly on target.
          const actualVisits = countStoreVisits(visits);
          const difference = actualVisits - expectedVisitsInPeriod;

          if (difference < -1) {
            underFrequencyCount++;
          } else if (difference > 1) {
            overFrequencyCount++;
          } else {
            conformFrequencyCount++;
          }

          if (target.visit_duration && target.visit_duration > 0) {
            storesWithDurationTargets++;

            const measured = measuredByStore.get(measureKey(sectorName, storeName)) || { total: 0, count: 0 };
            const avgDuration = measured.count > 0
              ? Math.round(measured.total / measured.count)
              : 0;
            const durationDifference = avgDuration - target.visit_duration;
            const durationDifferencePercentage = Math.abs(durationDifference) / target.visit_duration * 100;

            if (durationDifferencePercentage <= 15) {
              conformDurationCount++;
            } else if (durationDifference < 0) {
              underDurationCount++;
            } else {
              overDurationCount++;
            }
          }
        }
      });

      if (storesWithTargets > 0) {
        const frequencyScore = storesWithTargets > 0
          ? Math.round((conformFrequencyCount / storesWithTargets) * 100)
          : 0;

        const durationScore = storesWithDurationTargets > 0
          ? Math.round((conformDurationCount / storesWithDurationTargets) * 100)
          : 0;

        const underFrequencyPercent = storesWithTargets > 0
          ? Math.round((underFrequencyCount / storesWithTargets) * 100)
          : 0;
        const conformFrequencyPercent = storesWithTargets > 0
          ? Math.round((conformFrequencyCount / storesWithTargets) * 100)
          : 0;
        const overFrequencyPercent = storesWithTargets > 0
          ? Math.round((overFrequencyCount / storesWithTargets) * 100)
          : 0;

        const underDurationPercent = storesWithDurationTargets > 0
          ? Math.round((underDurationCount / storesWithDurationTargets) * 100)
          : 0;
        const conformDurationPercent = storesWithDurationTargets > 0
          ? Math.round((conformDurationCount / storesWithDurationTargets) * 100)
          : 0;
        const overDurationPercent = storesWithDurationTargets > 0
          ? Math.round((overDurationCount / storesWithDurationTargets) * 100)
          : 0;

        sectorAnalysisResult.push({
          sectorName,
          totalVisitedStores,
          storesWithTargets,
          storesWithDurationTargets,
          underFrequencyCount,
          conformFrequencyCount,
          overFrequencyCount,
          frequencyScore,
          underFrequencyPercent,
          conformFrequencyPercent,
          overFrequencyPercent,
          underDurationCount,
          conformDurationCount,
          overDurationCount,
          durationScore,
          underDurationPercent,
          conformDurationPercent,
          overDurationPercent
        });
      }
    });

    return sectorAnalysisResult.sort((a, b) => b.frequencyScore - a.frequencyScore);
  }, [visits, targets, startDate, endDate, selectedShopSigns]);

  const hasDurationTargets = useMemo(() => {
    return targets.some(target => target.visit_duration && target.visit_duration > 0);
  }, [targets]);

  return (
    <>
      {targetsLoading ? (
        <div className="col-span-1 lg:col-span-2 p-6 rounded-lg border dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Analyse par secteur
          </h3>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      ) : true ? (
        <div className="col-span-1 lg:col-span-2 p-6 rounded-lg border dark:border-gray-700">
          <h3 className="text-lg font-semibold mb-4 dark:text-white">
            Analyse par secteur
          </h3>
          <div className="overflow-x-auto">
            <SortableTable
              data={sectorAnalysis}
              columns={[
                { key: 'sectorName', label: 'Secteur' },
                { key: 'totalVisitedStores', label: 'PDV visités' },

                {
                  key: 'frequencyScore',
                  label: 'Fréquence conforme',
                  render: (item) => `${item.frequencyScore}%`,
                  sortFunction: (a, b, direction) => {
                    return direction === 'ascending'
                      ? a.frequencyScore - b.frequencyScore
                      : b.frequencyScore - a.frequencyScore;
                  }
                },
                {
                  key: 'underFrequencyCount',
                  label: 'Sous-visite',
                  render: (item) => `${item.underFrequencyPercent}% (${item.underFrequencyCount})`,
                  sortFunction: (a, b, direction) => {
                    return direction === 'ascending'
                      ? a.underFrequencyPercent - b.underFrequencyPercent
                      : b.underFrequencyPercent - a.underFrequencyPercent;
                  }
                },
                {
                  key: 'conformFrequencyCount',
                  label: 'Conforme (fréq.)',
                  render: (item) => `${item.conformFrequencyPercent}% (${item.conformFrequencyCount})`,
                  sortFunction: (a, b, direction) => {
                    return direction === 'ascending'
                      ? a.conformFrequencyPercent - b.conformFrequencyPercent
                      : b.conformFrequencyPercent - a.conformFrequencyPercent;
                  }
                },
                {
                  key: 'overFrequencyCount',
                  label: 'Sur-visite',
                  render: (item) => `${item.overFrequencyPercent}% (${item.overFrequencyCount})`,
                  sortFunction: (a, b, direction) => {
                    return direction === 'ascending'
                      ? a.overFrequencyPercent - b.overFrequencyPercent
                      : b.overFrequencyPercent - a.overFrequencyPercent;
                  }
                },

                ...(hasDurationTargets ? [
                  {
                    key: 'durationScore',
                    label: 'Durée conforme',
                    render: (item) => item.storesWithDurationTargets ? `${item.durationScore}%` : '-',
                    sortFunction: (a, b, direction) => {
                      return direction === 'ascending'
                        ? a.durationScore - b.durationScore
                        : b.durationScore - a.durationScore;
                    }
                  },
                  {
                    key: 'underDurationCount',
                    label: 'Sous-durée',
                    render: (item) => item.storesWithDurationTargets
                      ? `${item.underDurationPercent}% (${item.underDurationCount})`
                      : '-',
                    sortFunction: (a, b, direction) => {
                      return direction === 'ascending'
                        ? a.underDurationPercent - b.underDurationPercent
                        : b.underDurationPercent - a.underDurationPercent;
                    }
                  },
                  {
                    key: 'conformDurationCount',
                    label: 'Conforme (durée)',
                    render: (item) => item.storesWithDurationTargets
                      ? `${item.conformDurationPercent}% (${item.conformDurationCount})`
                      : '-',
                    sortFunction: (a, b, direction) => {
                      return direction === 'ascending'
                        ? a.conformDurationPercent - b.conformDurationPercent
                        : b.conformDurationPercent - a.conformDurationPercent;
                    }
                  },
                  {
                    key: 'overDurationCount',
                    label: 'Sur-durée',
                    render: (item) => item.storesWithDurationTargets
                      ? `${item.overDurationPercent}% (${item.overDurationCount})`
                      : '-',
                    sortFunction: (a, b, direction) => {
                      return direction === 'ascending'
                        ? a.overDurationPercent - b.overDurationPercent
                        : b.overDurationPercent - a.overDurationPercent;
                    }
                  }
                ] : [])
              ]}
              emptyMessage="Pas de secteur ou de targets définis"
              initialSortBy={{ key: 'conformFrequencyPercent', direction: 'descending' }}
              exportable={true}
              exportFilename="analyse_secteurs_visites.csv"
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default VisitsSectorAnalysis;
